import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  ChevronRight,
  Loader2,
  ShieldAlert,
  ShieldBan,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatRelative } from "date-fns";
import { ScreeningVerdictEnum, Permission } from "@forgepoint/shared";
import type { AgentSdkMessage, ScreeningVerdict } from "@forgepoint/shared";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveTask } from "@/lib/auth-utils";
import { getThreadScreeningResult } from "./utils";

interface ScreeningReviewCardProps {
  message: AgentSdkMessage;
  messages: AgentSdkMessage[];
  approvingTaskId: string | null;
  denyingTaskId: string | null;
  onOpenPanel: (messageId: string) => void;
  onApprove: (taskId: string) => void;
  onDeny: (taskId: string) => void;
}

/**
 * Returns the icon component for a screening verdict.
 * @param verdict - The screening verdict
 * @returns The icon component
 */
const getVerdictIcon = (verdict: ScreeningVerdict) => {
  return verdict === ScreeningVerdictEnum.BLOCK ? ShieldBan : ShieldAlert;
};

/**
 * Inline card rendered below a flagged user message to indicate screening status.
 * Clicking opens the MessageReviewPanel side panel.
 * @param props - The component props
 * @returns The rendered ScreeningReviewCard
 */
export const ScreeningReviewCard: React.FC<ScreeningReviewCardProps> = ({
  message,
  messages,
  approvingTaskId,
  denyingTaskId,
  onOpenPanel,
  onApprove,
  onDeny,
}) => {
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const canApprove = canUserApproveTask(user, message.task, canApproveAllTasks);

  const screeningResult = useMemo(
    () => getThreadScreeningResult(messages),
    [messages],
  );

  if (!screeningResult) {
    return null;
  }

  const isApproving = approvingTaskId === message.task?.id;
  const isDenying = denyingTaskId === message.task?.id;
  const isBlock = screeningResult.verdict === ScreeningVerdictEnum.BLOCK;
  const isRequiresReview = message.task?.status === "PENDING_REVIEW";
  const isCancelled = message.task?.status === "CANCELLED";
  const isCompleted = message.task?.status === "APPROVED";
  const Icon = getVerdictIcon(screeningResult.verdict);

  return (
    <div className="pl-11">
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            "group flex items-center justify-between",
            "border rounded-lg p-3 cursor-pointer transition-all duration-150 hover:bg-muted/50 hover:shadow-sm",
            isRequiresReview && "border-warning/50 bg-warning/5",
            isCancelled && "opacity-50",
            isCompleted && "border-success/50 bg-success/10",
          )}
          onClick={() => onOpenPanel(message.id)}
        >
          <div className="flex items-center gap-2">
            <Icon
              className={cn(
                "h-4 w-4",
                isBlock ? "text-destructive" : "text-warning",
              )}
            />
            <div>
              <div className="font-medium text-sm">
                {isBlock
                  ? "Blocked External Message"
                  : "Flagged External Message"}
              </div>
              <div className="text-xs text-muted-foreground">
                <Badge
                  variant={isBlock ? "destructive" : "default"}
                  className={cn(
                    "text-xs mr-1",
                    !isBlock && "bg-warning/15 text-warning border-warning/30",
                  )}
                >
                  {screeningResult.verdict}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            {isRequiresReview && (
              <span className="text-xs text-muted-foreground group-hover:opacity-100 transition-opacity opacity-0">
                Pending since {formatRelative(message.createdAt, new Date())}
              </span>
            )}
            <ChevronRight className="h-4 w-4" />
          </div>
        </div>
        {isRequiresReview && message.task && canApprove && (
          <div className="flex gap-2">
            <Button
              className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
              onClick={() => onApprove(message.task!.id)}
              disabled={isApproving || isDenying}
            >
              {isApproving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Approve
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => onDeny(message.task!.id)}
              disabled={isApproving || isDenying}
            >
              {isDenying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
